import PropTypes from 'prop-types';
import EverTrue from 'app';
import ListSource from 'apps/lists/sources/list-source';
import ListContactSource from 'apps/lists/sources/list-contact-source';
import ContactSource from 'apps/contact/sources/contact-source';
import ExportSearchController from 'apps/export/controllers/export-search-controller';
import ListShareSettings from 'apps/lists/components/list-share-settings';
import BulkListController from 'apps/lists/controllers/bulk-list-controller';
import EmmaPushController from 'apps/lists/components/emma-push-controller';
import AddToFolderConfirmModal from 'apps/folder/components/modal/add-to-folder-confirm-modal';
import { useIdentity } from 'base/identity-resolver';
import { useGate } from 'components/is-gated';
import { Modal, ModalConfirm, ActionsMenu } from '@evertrue/et-components';
import { useFluxStore } from '@evertrue/et-flux';
import MapStore from 'apps/map/stores/map-store';
import ListsSelectorController from '../controllers/lists-selector-controller';
import NewListForm from './new-list-form';
import { isEmpty } from '@evertrue/sdk';

const ListActions = ({
  list = {},
  resultCount = 0,
  selectedContacts = [],
  onRemove = () => {},
  emma_accounts = [],
  emma_groups = [],
  onEmmaChange = () => {},
  onCreateList = () => {},
  isAllSelected = false,
  filterParams = {},
  hasActiveFilters = false,
}) => {
  const { user } = useIdentity();
  const [hasEmma] = useGate('emma');
  const bounding_box = useFluxStore(MapStore, () => MapStore.getBoundingBox());

  const isOwner = list?.user_id === user?.id;
  const selectedCount = selectedContacts?.length || 0;

  const getOptions = () => {
    const options = [];
    const items = [];

    if (selectedCount > 0 && !isAllSelected) {
      items.push({
        id: 'add-to-another-list',
        icon: 'playlist-add',
        label: `Add ${selectedCount} Selected Constituents to Another List`,
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close} size="small">
            <ModalConfirm header="Add Constituents to List" onCancel={close}>
              <ListsSelectorController contactIds={selectedContacts} requestHide={close} />
            </ModalConfirm>
          </Modal>
        ),
      });
    }
    if (selectedCount > 0 || isAllSelected) {
      items.push({
        id: 'remove-from-list',
        icon: 'playlist-minus',
        label: `Remove ${selectedCount} Selected Constituents From List`,
        onClick: onRemove,
      });
    }

    items.push(
      {
        id: 'manage-collaborators',
        icon: 'settings',
        label: 'Manage Collaborators',
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close} size="medium">
            <ModalConfirm header="Sharing Settings" className="share-settings--modal" onCancel={close}>
              <ListShareSettings listId={list?.id} />
            </ModalConfirm>
          </Modal>
        ),
      },
      {
        id: 'export-list',
        icon: 'cloud-download',
        label: `Export ${resultCount} Constituents`,
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close} size="medium">
            <ExportSearchController
              exportType="List"
              onExport={(propertyIds) => {
                if (hasActiveFilters) {
                  return ContactSource.export(propertyIds, { search: filterParams });
                } else if (bounding_box && !isEmpty(bounding_box)) {
                  // Map view with geographic filtering
                  const searchWithMap = {
                    must: [{ 'list_memberships.list_id': { match: list?.id } }, { 'addresses.location': bounding_box }],
                  };
                  return ContactSource.export(propertyIds, { search: searchWithMap });
                } else {
                  return ListContactSource.export(list?.id, propertyIds);
                }
              }}
              exportText={
                <div>
                  <div className="text-block">You are about to export all constituent records for the list:</div>
                  <strong>{list?.name}</strong>
                </div>
              }
              requestHide={close}
            />
          </Modal>
        ),
      },
      {
        id: 'add-constituents',
        icon: 'add',
        label: 'Add Constituents to List',
        mountOnClick: ({ is_open, close }) => <BulkListController list={list} remove={close} />,
      }
    );

    if (hasEmma) {
      items.push({
        id: 'send-to-emma',
        icon: 'email',
        label: 'Send List to Emma',
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close} size="medium">
            <EmmaPushController
              list={list}
              listResultCount={resultCount}
              accounts={emma_accounts}
              groups={emma_groups}
              onSuccess={onEmmaChange}
              onError={onEmmaChange}
              requestHide={close}
            />
          </Modal>
        ),
      });
    }

    if (isOwner && list?.name !== 'Favorites') {
      items.push({
        id: 'delete-list',
        icon: 'delete',
        className: 'text-danger',
        label: 'Delete List',
        onClick: () => {
          EverTrue.Alert.confirm(
            {
              content: 'Deleted lists cannot be recovered. Are you sure you want to delete this list?',
              headline: (
                <span>
                  Delete <strong>{list?.name}</strong>
                </span>
              ),
              buttonLabel: 'Delete List',
            },
            (didConfirm) => {
              if (didConfirm) {
                ListSource.delete(list?.id);
              }
            }
          );
        },
      });
    }

    items.push({
      id: 'add-to-folder',
      icon: 'folder',
      iconKit: 'gt2',
      label: 'Add List to Folder',
      mountOnClick: ({ is_open, close }) => (
        <Modal isOpen={is_open} closeModal={close} size="dynamic">
          <AddToFolderConfirmModal requestHide={close} list={list} />
        </Modal>
      ),
    });

    if (selectedCount > 0 || isAllSelected) {
      items.push({
        id: 'save-another-list',
        icon: 'playlist-add',
        label: 'Save as New List',
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close} size="small">
            <div style={{ padding: '16px' }}>
              <NewListForm
                isOpen={is_open}
                onClose={close}
                onSubmit={(...args) => {
                  onCreateList(...args);
                  close();
                }}
              />
            </div>
          </Modal>
        ),
      });
    }

    options.push({ id: 'list-actions', items });
    return options;
  };

  return <ActionsMenu options={getOptions()} />;
};

ListActions.propTypes = {
  list: PropTypes.object,
  resultCount: PropTypes.number,
  selectedContacts: PropTypes.array,
  onRemove: PropTypes.func,
  emma_accounts: PropTypes.array,
  emma_groups: PropTypes.array,
  onEmmaChange: PropTypes.func,
  onCreateList: PropTypes.func,
  isAllSelected: PropTypes.bool,
  filterParams: PropTypes.object,
  hasActiveFilters: PropTypes.bool,
};

export default ListActions;
